import type { FC, ComponentType } from "react";

import { Outlet } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics as VercelAnalytics } from "@vercel/analytics/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Route, Routes, useLocation, BrowserRouter } from "react-router-dom";

// Import landing page directly to ensure instant loading
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";
import AuthGuard from "./contexts/auth/auth-guard";
import CookieConsent from "./pages/CookieConsent.tsx";
import lazyWithPreload from "./util/lazy-with-preload";
import { GuestGuard } from "./contexts/auth/guest-guard";
import { SplashScreen } from "./components/loading-screen";
import { AuthProvider } from "./contexts/auth/auth-provider";
import { OnboardingGuard } from "./contexts/auth/onboarding-guard";
import { WorkspaceProvider } from "./contexts/workspace/workspace-provider";
import RegistrationConfirmation from "./pages/auth/RegistrationConfirmation";

// Component for wrapping lazy-loaded components
interface LazyComponentProps {
  component: ComponentType<any>;
  [key: string]: any;
}

const LazyComponent: FC<LazyComponentProps> = ({
  component: Component,
  ...props
}) => (
  <Suspense fallback={<SplashScreen />}>
    <Component {...props} />
  </Suspense>
);

// Create QueryClient instance
const queryClient = new QueryClient();

// Landing and public pages bundle - Lazy loaded but with high priority
const Features = lazyWithPreload(
  () => import(/* webpackChunkName: "landing-features" */ "./pages/Features")
);
const Pricing = lazyWithPreload(
  () => import(/* webpackChunkName: "landing-pricing" */ "./pages/Pricing")
);
const SubscriptionSuccess = lazyWithPreload(
  () =>
    import(/* webpackChunkName: "subscription" */ "./pages/SubscriptionSuccess")
);
const SubscriptionCancel = lazyWithPreload(
  () =>
    import(/* webpackChunkName: "subscription" */ "./pages/SubscriptionCancel")
);
const About = lazyWithPreload(
  () => import(/* webpackChunkName: "landing-about" */ "./pages/About")
);
const Blogs = lazyWithPreload(
  () => import(/* webpackChunkName: "landing-blogs" */ "./pages/Blogs")
);
const BlogPost1 = lazyWithPreload(
  () => import(/* webpackChunkName: "blog-post" */ "./pages/BlogPost1")
);
const BlogPost2 = lazyWithPreload(
  () => import(/* webpackChunkName: "blog-post" */ "./pages/BlogPost2")
);
const BlogPost3 = lazyWithPreload(
  () => import(/* webpackChunkName: "blog-post" */ "./pages/BlogPost3")
);
const BlogPost4 = lazyWithPreload(
  () => import(/* webpackChunkName: "blog-post" */ "./pages/BlogPost4")
);
const BlogPost5 = lazyWithPreload(
  () => import(/* webpackChunkName: "blog-post" */ "./pages/BlogPost5")
);
const BlogPost6 = lazyWithPreload(
  () => import(/* webpackChunkName: "blog-post" */ "./pages/BlogPost6")
);
const BlogPost7 = lazyWithPreload(
  () => import(/* webpackChunkName: "blog-post" */ "./pages/BlogPost7")
);
const BlogPost8 = lazyWithPreload(
  () => import(/* webpackChunkName: "blog-post" */ "./pages/BlogPost8")
);
const Contact = lazyWithPreload(
  () => import(/* webpackChunkName: "landing-contact" */ "./pages/Contact")
);
const Feedback = lazyWithPreload(
  () => import(/* webpackChunkName: "landing-feedback" */ "./pages/Feedback")
);
const PrivacyPolicy = lazyWithPreload(
  () => import(/* webpackChunkName: "landing-legal" */ "./pages/PrivacyPolicy")
);
const TermsOfService = lazyWithPreload(
  () => import(/* webpackChunkName: "landing-legal" */ "./pages/TermsOfService")
);
const PublicForm = lazyWithPreload(
  () => import(/* webpackChunkName: "public-form" */ "./pages/PublicForm")
);
const FormDemo = lazyWithPreload(
  () => import(/* webpackChunkName: "form-demo" */ "./pages/FormDemo")
);
const Invite = lazyWithPreload(
  () => import(/* webpackChunkName: "invite" */ "./pages/Invite")
);

// Auth pages bundle - Lazy loaded
const Login = lazy(
  () => import(/* webpackChunkName: "auth" */ "./pages/auth/Login")
);
const Signup = lazy(
  () => import(/* webpackChunkName: "auth" */ "./pages/auth/Signup")
);
const ForgotPassword = lazy(
  () => import(/* webpackChunkName: "auth" */ "./pages/auth/ForgotPassword")
);
const UpdatePassword = lazy(
  () => import(/* webpackChunkName: "auth" */ "./pages/auth/UpdatePassword")
);
const VerifyEmail = lazy(
  () => import(/* webpackChunkName: "auth" */ "./pages/auth/Verify")
);
const Onboarding = lazy(
  () => import(/* webpackChunkName: "auth" */ "./pages/onboarding/Onboarding")
);

// Error pages bundle
const Page403 = lazy(
  () => import(/* webpackChunkName: "error" */ "./pages/error/403")
);
const Page404 = lazy(
  () => import(/* webpackChunkName: "error" */ "./pages/error/404")
);
const Page500 = lazy(
  () => import(/* webpackChunkName: "error" */ "./pages/error/500")
);

// Dashboard pages - Split into separate bundles
const Dashboard = lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard-main" */ "./pages/dashboard/Dashboard"
    )
);
const Forms = lazy(
  () =>
    import(/* webpackChunkName: "dashboard-main" */ "./pages/dashboard/Forms")
);
const FormBuilder = lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard-form" */ "./pages/dashboard/FormBuilder"
    )
);
const FormSubmissions = lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard-form-submissions" */ "./pages/dashboard/FormSubmissions"
    )
);
const SubmissionDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard-form-submissions" */ "./pages/dashboard/SubmissionDetail"
    )
);
const Templates = lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard-secondary" */ "./pages/dashboard/Templates"
    )
);
const Analytics = lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard-secondary" */ "./pages/dashboard/Analytics"
    )
);
const Team = lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard-secondary" */ "./pages/dashboard/Team"
    )
);
const Settings = lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard-secondary" */ "./pages/dashboard/Settings"
    )
);
// const Notifications = lazy(
//   () =>
//     import(
//       /* webpackChunkName: "dashboard-secondary" */ "./pages/dashboard/Notifications"
//     )
// );
const AuditLogs = lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard-secondary" */ "./pages/dashboard/AuditLogs"
    )
);
const Workspaces = lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard-secondary" */ "./pages/dashboard/Workspaces"
    )
);
const CreateWorkspace = lazy(
  () =>
    import(
      /* webpackChunkName: "dashboard-secondary" */ "./pages/dashboard/CreateWorkspace"
    )
);

// Preload bundles for faster navigation
const landingBundle = {
  preload: () => {
    Features.preload();
    Pricing.preload();
    SubscriptionSuccess.preload();
    SubscriptionCancel.preload();
    About.preload();
    Blogs.preload();
    BlogPost1.preload();
    BlogPost2.preload();
    BlogPost3.preload();
    BlogPost4.preload();
    BlogPost5.preload();
    BlogPost6.preload();
    BlogPost7.preload();
    BlogPost8.preload();
    Contact.preload();
    Feedback.preload();
    PrivacyPolicy.preload();
    TermsOfService.preload();
    Invite.preload();
    FormDemo.preload();
  },
};

// Component for handling preloading
const PreloadHandler = () => {
  const location = useLocation();

  useEffect(() => {
    // Preload landing pages when on homepage
    if (location.pathname === "/") {
      // Preload with a slight delay to prioritize current page rendering
      const timeoutId = setTimeout(() => {
        landingBundle.preload();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }

    // Mouse hover preloading for navigation links
    if (
      location.pathname === "/" ||
      location.pathname.includes("/features") ||
      location.pathname.includes("/pricing") ||
      location.pathname.includes("/about")
    ) {
      // Add hover listeners to dashboard-related links
      const dashboardLinks = document.querySelectorAll(
        'a[href^="/dashboard"], a[href^="/auth"]'
      );
      const handleMouseEnter = (event) => {
        const href = event.currentTarget.getAttribute("href");
        if (href.startsWith("/dashboard")) {
          import(
            /* webpackChunkName: "dashboard-main" */ "./pages/dashboard/Dashboard"
          );
        } else if (href.startsWith("/auth")) {
          import(/* webpackChunkName: "auth" */ "./pages/auth/Login");
        }
      };

      dashboardLinks.forEach((link) => {
        link.addEventListener("mouseenter", handleMouseEnter);
      });

      return () => {
        dashboardLinks.forEach((link) => {
          link.removeEventListener("mouseenter", handleMouseEnter);
        });
      };
    }
  }, [location]);

  return null;
};

// DashboardLayout component to wrap Dashboard routes
const DashboardLayout = () => (
  <AuthGuard>
    <OnboardingGuard>
      <Outlet />
    </OnboardingGuard>
  </AuthGuard>
);

// App component
const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <SpeedInsights />
        <VercelAnalytics />
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <CookieConsent />
          <WorkspaceProvider>
            <ScrollToTop />
            <PreloadHandler />
            <Routes>
              {/* Error pages */}
              <Route
                path="/403"
                element={<LazyComponent component={Page403} />}
              />
              <Route
                path="/500"
                element={<LazyComponent component={Page500} />}
              />

              {/* Landing pages - Index is imported directly for instant load */}
              <Route path="/" element={<Index />} />
              <Route
                path="/features"
                element={<LazyComponent component={Features} />}
              />
              <Route
                path="/pricing"
                element={<LazyComponent component={Pricing} />}
              />
              <Route
                path="/about"
                element={<LazyComponent component={About} />}
              />
              <Route
                path="/privacy"
                element={<LazyComponent component={PrivacyPolicy} />}
              />
              <Route
                path="/terms"
                element={<LazyComponent component={TermsOfService} />}
              />
              <Route
                path="/contact"
                element={<LazyComponent component={Contact} />}
              />
              <Route
                path="/feedback"
                element={<LazyComponent component={Feedback} />}
              />
              <Route
                path="/blogs"
                element={<LazyComponent component={Blogs} />}
              />
              <Route
                path="/blogs/1"
                element={<LazyComponent component={BlogPost1} />}
              />
              <Route
                path="/blogs/2"
                element={<LazyComponent component={BlogPost2} />}
              />
              <Route
                path="/blogs/3"
                element={<LazyComponent component={BlogPost3} />}
              />
              <Route
                path="/blogs/4"
                element={<LazyComponent component={BlogPost4} />}
              />
              <Route
                path="/blogs/5"
                element={<LazyComponent component={BlogPost5} />}
              />
              <Route
                path="/blogs/6"
                element={<LazyComponent component={BlogPost6} />}
              />
              <Route
                path="/blogs/7"
                element={<LazyComponent component={BlogPost7} />}
              />
              <Route
                path="/blogs/8"
                element={<LazyComponent component={BlogPost8} />}
              />

              {/* Auth routes - Protected with GuestGuard */}
              <Route path="/auth">
                <Route
                  path="login"
                  element={
                    <GuestGuard>
                      <LazyComponent component={Login} />
                    </GuestGuard>
                  }
                />
                <Route
                  path="signup"
                  element={
                    <GuestGuard>
                      <LazyComponent component={Signup} />
                    </GuestGuard>
                  }
                />
                <Route
                  path="forgot-password"
                  element={
                    <GuestGuard>
                      <LazyComponent component={ForgotPassword} />
                    </GuestGuard>
                  }
                />
                <Route
                  path="update-password"
                  element={
                    <GuestGuard>
                      <LazyComponent component={UpdatePassword} />
                    </GuestGuard>
                  }
                />
                <Route
                  path="verify-email"
                  element={
                    <GuestGuard>
                      <LazyComponent component={VerifyEmail} />
                    </GuestGuard>
                  }
                />
                <Route
                  path="confirm-registration"
                  element={
                    <AuthGuard>
                      <LazyComponent component={RegistrationConfirmation} />
                    </AuthGuard>
                  }
                />
              </Route>

              <Route
                path="/onboarding"
                element={
                  <AuthGuard>
                    <LazyComponent component={Onboarding} />
                  </AuthGuard>
                }
              />

              {/* Dashboard Routes - All protected with AuthGuard */}
              <Route element={<DashboardLayout />}>
                {/* Default dashboard route - will redirect to a workspace */}
                <Route
                  path="/dashboard"
                  element={<LazyComponent component={Dashboard} />}
                />

                {/* Workspace-specific routes */}
                <Route path="/dashboard/:workspaceId">
                  <Route
                    index
                    element={<LazyComponent component={Dashboard} />}
                  />
                  <Route
                    path="forms"
                    element={<LazyComponent component={Forms} />}
                  />
                  <Route
                    path="forms/builder"
                    element={<LazyComponent component={FormBuilder} />}
                  />
                  <Route
                    path="forms/builder/:formId"
                    element={<LazyComponent component={FormBuilder} />}
                  />
                  <Route
                    path="forms/:formId/submissions"
                    element={<LazyComponent component={FormSubmissions} />}
                  />
                  <Route
                    path="forms/:formId/submissions/:submissionId"
                    element={<LazyComponent component={SubmissionDetail} />}
                  />
                  <Route
                    path="templates"
                    element={<LazyComponent component={Templates} />}
                  />
                  <Route
                    path="analytics"
                    element={<LazyComponent component={Analytics} />}
                  />
                  <Route
                    path="team"
                    element={<LazyComponent component={Team} />}
                  />
                  <Route
                    path="settings"
                    element={<LazyComponent component={Settings} />}
                  />
                  <Route
                    path="audit-logs"
                    element={<LazyComponent component={AuditLogs} />}
                  />
                </Route>

                {/* Global routes (not workspace-specific) */}
                {/* <Route
                  path="/dashboard/notifications"
                  element={<LazyComponent component={Notifications} />}
                /> */}

                <Route
                  path="/dashboard/create-workspace"
                  element={<LazyComponent component={CreateWorkspace} />}
                />

                {/* Workspaces route is separate since it doesn't belong to a specific workspace */}
                <Route
                  path="/workspaces"
                  element={<LazyComponent component={Workspaces} />}
                />
              </Route>

              {/* Form Demo Route */}
              <Route
                path="/form-demo"
                element={<LazyComponent component={FormDemo} />}
              />

              {/* Invite Route */}
              <Route
                path="/invite"
                element={<LazyComponent component={Invite} />}
              />

              {/* Subscription Routes */}
              <Route
                path="/subscription/success"
                element={<LazyComponent component={SubscriptionSuccess} />}
              />
              <Route
                path="/subscription/cancel"
                element={<LazyComponent component={SubscriptionCancel} />}
              />

              {/* Public Form Routes */}
              <Route
                path="/forms/:formId"
                element={<LazyComponent component={PublicForm} />}
              />
              <Route
                path="/public-form-preview/:formId"
                element={
                  <LazyComponent component={PublicForm} isPreview={true} />
                }
              />

              {/* 404 route */}
              <Route path="*" element={<LazyComponent component={Page404} />} />
            </Routes>
          </WorkspaceProvider>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
